import "./StatsCounter.css";

import { animate, stagger } from "animejs";
import { useEffect, useRef, useState } from "react";

import { settle,useRevealScene } from "@/4-components/animations/anime";
import { useSpotlight } from "@/4-components/animations/pointer";
import { DURATION, EASE, STAGGER } from "@/5-shared/constants/motion";
import type { Stat } from "@/5-shared/constants/content/types";

import type { Layer } from "./field/core";
import { EnergyField } from "./field/EnergyField";

const SHIMMER_MS = 900;

/** Couples selectable figures to the matching energy-field layers. */
export function StatsCounter({ stats, layers }: { stats: Stat[]; layers?: Layer[] }) {
  const [active, setActive] = useState(0);
  const [hot, setHot] = useState(false);

  const rowRef = useRevealScene<HTMLDivElement>(
    ({ all }) => {
      const cells = all<HTMLElement>(".stat-cell");
      const values = all<HTMLElement>(".stat-value");
      if (cells.length === 0) return;

      return {
        still: () => {
          settle(cells);
          cells.forEach((el) => (el.style.filter = "none"));
        },
        play: (add) => {
          add(
            animate(cells, {
              opacity: [0, 1],
              translateY: [14, 0],
              filter: ["blur(10px)", "blur(0px)"],
              duration: DURATION.cell,
              delay: stagger(STAGGER.base),
              ease: EASE.out,
            }),
          );
          add(
            animate(values, {
              backgroundPositionX: ["120%", "-20%"],
              duration: SHIMMER_MS,
              delay: stagger(STAGGER.base, { start: 180 }),
              ease: EASE.inOut,
            }),
          );
          values.forEach((el, i) => add(rollUp(el, 180 + i * 90)));
        },
      };
    },
    [stats],
  );

  return (
    <section className="stats-counter relative isolate overflow-hidden bg-zinc-950 pt-[clamp(5rem,4.6939rem+1.3061vw,6rem)]">
      <div className="shell relative z-10">
        <div ref={rowRef} data-odd={stats.length % 2 !== 0} className="stats-counter__row grid gap-y-12 md:flex md:justify-between">
          {stats.map((stat, index) => (
            <StatCell key={stat.label} stat={stat} active={index === active} onSelect={() => setActive(index)} onHover={setHot} />
          ))}
        </div>
      </div>
      <div className="shell">
        <EnergyField active={active} hot={hot} layers={layers} />
      </div>
    </section>
  );
}

/**
 * Counts every number inside the value up from 0 while the rest of the string
 * ("50-60"'s dash) stays put. The markup keeps the final text, so no-JS,
 * reduced motion and the SSR harness all read the real figure.
 */
function rollUp(el: HTMLElement, delay: number) {
  const finals = (el.dataset.final ??= el.textContent ?? "");
  const at = { p: 0 };
  const paint = () => (el.textContent = finals.replace(/\d+/g, (n) => String(Math.round(Number(n) * at.p))));
  paint();
  return animate(at, {
    p: 1,
    duration: DURATION.rollUp,
    delay,
    ease: EASE.outSharp,
    onUpdate: paint,
    onComplete: () => (el.textContent = finals),
  });
}

function StatCell({ stat, active, onSelect, onHover }: { stat: Stat; active: boolean; onSelect: () => void; onHover: (hovering: boolean) => void }) {
  const ref = useSpotlight<HTMLButtonElement>();
  // Each cell owns its timer so a prior click cannot cancel a newer shimmer.
  const lit = useRef(0);
  useEffect(() => () => window.clearTimeout(lit.current), []);

  const select = (event: React.MouseEvent<HTMLButtonElement>) => {
    onSelect();
    const el = event.currentTarget;
    el.setAttribute("data-lit", "");
    window.clearTimeout(lit.current);
    lit.current = window.setTimeout(() => el.removeAttribute("data-lit"), SHIMMER_MS);
  };

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      onClick={select}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      className="stat-cell group relative rounded-2xl p-4 text-left opacity-0 outline-none transition-transform duration-300 hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-dec-green-light"
    >
      <span
        aria-hidden="true"
        className="stat-spotlight pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />
      <div className="relative font-display font-bold tracking-tight">
        {stat.prefix && <span className="block text-sm font-medium text-zinc-400 md:text-base">{stat.prefix}</span>}
        <span className="flex items-baseline whitespace-nowrap">
          <span className="stat-value text-5xl tabular-nums md:text-6xl">{stat.target}</span>
          <span className="stat-suffix ml-1 text-3xl transition-colors duration-500 md:text-4xl">{stat.suffix}</span>
        </span>
      </div>
      <p className="stat-label relative mt-3 max-w-[13rem] text-sm leading-snug transition-colors duration-500 md:text-base">{stat.label}</p>
    </button>
  );
}
